/**
 * Ensures a number is positive.
 * @param number - The input number to check.
 * @returns The positive number or 0 if input is not provided or not positive.
 */
export function ensurePositive(number: number | undefined) {
	if (number === undefined) {
		return 0;
	}

	return number > 0 ? number : 0;
}

/**
 * Checks if a value is equivalent to true.
 * @param value - The value to check.
 * @returns True if the value is equivalent to true, false otherwise.
 */
export function isTrue(value: unknown): boolean {
	if (typeof value === 'string') {
		return value.toLowerCase() === 'true';
	}
	if (typeof value === 'boolean') {
		return value;
	}
	return false;
}

/**
 * Checks if a string is valid JSON.
 * @param str - The string to check.
 * @returns True if the string is valid JSON, false otherwise.
 */
export function isJson(str?: string | null): boolean {
	if (!str) {
		return false;
	}
	try {
		const o = JSON.parse(str);
		if (o && typeof o === 'object') {
			return true;
		}
	} catch (e) {
		return false;
	}
	return false;
}

/**
 * Generates SHA-1 hash of a string.
 * @param message - The message to hash.
 * @returns Promise resolving to SHA-1 hash as hex string.
 */
export function sha1(message: string): Promise<string> {
	const utf8 = new TextEncoder().encode(message);
	return crypto.subtle.digest('SHA-1', utf8).then((hashBuffer) => {
		const hashArray = Array.from(new Uint8Array(hashBuffer));
		const hashHex = hashArray
			.map((bytes) => bytes.toString(16).padStart(2, '0'))
			.join('');
		return hashHex;
	});
}

/**
 * Generates SHA-256 hash of a string.
 * @param message - The message to hash.
 * @returns Promise resolving to SHA-256 hash as hex string.
 */
export function sha256(message: string): Promise<string> {
	const utf8 = new TextEncoder().encode(message);
	return crypto.subtle.digest('SHA-256', utf8).then((hashBuffer) => {
		const hashArray = Array.from(new Uint8Array(hashBuffer));
		const hashHex = hashArray
			.map((bytes) => bytes.toString(16).padStart(2, '0'))
			.join('');
		return hashHex;
	});
}

/**
 * Computes hash code of a string.
 * @param str - The string to compute hash code for.
 * @returns The computed hash code.
 */
export function hashCode(str: string): number {
	let hash = 0;
	for (let i = 0; i < str.length; i += 1) {
		// eslint-disable-next-line no-bitwise
		hash = str.charCodeAt(i) + ((hash << 5) - hash);
	}
	return hash;
}

/**
 * Checks if a URL is an internal link.
 * @param url - The URL to check.
 * @returns True if the URL is internal, false otherwise.
 * @note Returns false when called server-side.
 */
export function isInternalUrl(url: string): boolean {
	// Empty strings are never internal
	if (url === '') {
		return false;
	}

	// Relative paths are always internal
	if (url[0] === '/') {
		return true;
	}

	const linkHost =
		typeof window !== 'undefined' ? window.location.host : undefined;

	const currentHost = new URL(url).host;
	return linkHost === currentHost;
}

export type ObjectValues<T> = T[keyof T];

/**
 * Creates a promise that resolves after specified milliseconds.
 * @param ms - Milliseconds to wait.
 * @returns Promise that resolves after timeout.
 */
export function timeout(ms: number | undefined) {
	return new Promise((resolve) => setTimeout(resolve, ms));
}

/**
 * Checks if email is from secoda.co domain.
 * This domain is used for impersonating customer workspaces.
 * @param email - The email to check.
 * @returns True if email ends with @secoda.co.
 */
export function isSecodaCoEmail(email?: string): boolean {
	return email?.endsWith('@secoda.co') ?? false;
}

/**
 * Checks if email is from secoda.com domain.
 * This domain is used for self-serve flow (Secoda AI/Lite).
 * @param email - The email to check.
 * @returns True if email ends with @secoda.com.
 */
export function isSecodaComEmail(email?: string): boolean {
	return email?.endsWith('@secoda.com') ?? false;
}
